var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import Panel from "@/components/ui/panel/Panel.vue";
import Button from "@/components/ui/button/Button.vue";
import ExportButton from "@/core/export/ExportButton.vue";
import { fetchResultsActionAvailability } from "@/core/period";
import PeriodSelector from "@/core/period/PeriodSelector.vue";
import { useReportDataset } from "@/core/report";
import DatasetFilter from "@/core/report/components/DatasetFilter.vue";
import DatasetTable from "@/core/report/components/DatasetTable.vue";
import PaginatorControls from "@/core/report/components/PaginatorControls.vue";
import PaginatorInfo from "@/core/report/components/PaginatorInfo.vue";
import PaginatorItemsPerPage from "@/core/report/components/PaginatorItemsPerPage.vue";
import SortContextMenu from "@/core/report/components/SortContextMenu.vue";
import TableZoomDropdown from "@/core/report/components/TableZoomDropdown.vue";
import { useTableZoom } from "@/core/report/use-table-zoom";
import FieldBulkEditDialog from "./features/field-bulk-edit/FieldBulkEditDialog.vue";
import FieldChangeReviewDialog from "./features/field-change-review/FieldChangeReviewDialog.vue";
import { computed, onMounted, ref, watch } from "vue";
import { fetchPendingChangesForTable } from "./api/changes";
import { UploadIcon, ClipboardCheckIcon } from "lucide-vue-next";
import { fetchReportData } from "./api";
import { FORMULAS } from "@/lib/formulas";
import { fetchObjectiveTitleMaps } from "./api/objectives";
import { DEFAULT_SORTS, FIELDS, PRESET_SORTS, SORTABLE_FIELDS, getFilterFields, getTableFields } from "./config";
import LoadingSpinner from "@/components/app/LoadingSpinner.vue";
import { useChangeReview } from "./features/field-change-review/use-change-review";
import { currentUserHasRole, currentUserHasAnyRole } from "@/core/users/roles";
import { currentUser } from "@/core/users/session";
import { groupLevanteData } from "./lib/zones/levante";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ResultsReportView',
    setup(__props, { expose: __expose }) {
        __expose();
        const { tableZoom } = useTableZoom();
        // Initialize with current month/year
        const selectedPeriod = ref(null);
        // Data state
        const isLoading = ref(false);
        const error = ref(null);
        const rawTableData = ref([]);
        const tab = ref("");
        const actionAvailability = ref(null);
        const isNorthReadonlyUser = computed(() => currentUser().area_code === "N" && !currentUser().region_code);
        const hasRedRevisada = computed(() => ["N100", "N300", "E100", "E300"].includes(currentUser().region_code) && !['catalunya_rpv'].includes(tab.value));
        const canRequestResultsChanges = computed(() => hasRedRevisada.value && !isNorthReadonlyUser.value && currentUserHasAnyRole(["Gestor de Distribuidor", "Distribuidor"]));
        const canValidateResultsChanges = computed(() => hasRedRevisada.value && !isNorthReadonlyUser.value && currentUserHasRole("Validador"));
        const canBulkEditResults = computed(() => { var _a; return canRequestResultsChanges.value && Boolean((_a = actionAvailability.value) === null || _a === void 0 ? void 0 : _a.canEdit); });
        const canReviewResults = computed(() => { var _a; return canValidateResultsChanges.value && Boolean((_a = actionAvailability.value) === null || _a === void 0 ? void 0 : _a.canValidate); });
        // Pending changes state
        const pendingChanges = ref([]);
        // Change review
        const changeReview = useChangeReview();
        const reviewDialogRef = ref();
        // Computed: Pending change requests count
        const pendingChangeRequestsCount = computed(() => changeReview.changeRequests.value.length);
        const isLevanteRegionUser = computed(() => currentUser().region_code === "E300");
        const isCanariasRegionUser = computed(() => currentUser().region_code === "S300");
        const isMadridRegionUser = computed(() => currentUser().region_code === "C100");
        // Computed table fields que se adaptan según los datos
        const tableData = computed(() => {
            if (isLevanteRegionUser.value) {
                return groupLevanteData(rawTableData.value);
            }
            if (isCanariasRegionUser.value) {
                return addCanariasDelegationTotals(rawTableData.value);
            }
            if (isMadridRegionUser.value) {
                return addMadridDelegationTotals(rawTableData.value);
            }
            return rawTableData.value;
        });
        const TABLE_FIELDS = ref([]);
        const filterFields = getFilterFields();
        const displayPendingChanges = computed(() => isLevanteRegionUser.value ? [] : pendingChanges.value);
        const dialogRows = computed(() => isLevanteRegionUser.value ? rawTableData.value : tableData.value);
        const bulkEditRows = computed(() => dialogRows.value.map((row) => ({
            delegacion: row.delegacion,
            sala: row.sala,
            distribuidor: row.distribuidor,
            delegacion_zona_de_ventas: row.delegacion_zona_de_ventas,
        })));
        const sumByPath = (rows, path) => {
            return rows.reduce((total, row) => {
                const value = path.split(".").reduce((current, segment) => current === null || current === void 0 ? void 0 : current[segment], row);
                const numericValue = Number(value);
                return Number.isFinite(numericValue) ? total + numericValue : total;
            }, 0);
        };
        const calculateWeightedCanariasProgress = (currentValue, objectiveValue) => {
            if (objectiveValue <= 0) {
                return 0;
            }
            if (currentValue > objectiveValue) {
                return 0.25;
            }
            const progress = currentValue / objectiveValue;
            return progress > 0.8 ? progress * 0.25 : 0;
        };
        const addCanariasDelegationTotals = (rows) => {
            const result = [];
            const rowsByDelegation = new Map();
            rows.forEach((row) => {
                var _a, _b;
                const delegation = (_a = row.delegacion) !== null && _a !== void 0 ? _a : "";
                rowsByDelegation.set(delegation, [...((_b = rowsByDelegation.get(delegation)) !== null && _b !== void 0 ? _b : []), row]);
            });
            rowsByDelegation.forEach((delegationRows, delegation) => {
                var _a, _b;
                result.push(...delegationRows.map((row) => (Object.assign(Object.assign({}, row), { is_total_row: false }))));
                const totalRow = {
                    delegacion: delegation,
                    sala: "",
                    delegacion_zona_de_ventas: `TOTAL ${delegation}`,
                    distribuidor: `TOTAL ${delegation}`,
                    is_total_row: true,
                    ano_natural: (_a = delegationRows[0]) === null || _a === void 0 ? void 0 : _a.ano_natural,
                    mes_natural: (_b = delegationRows[0]) === null || _b === void 0 ? void 0 : _b.mes_natural,
                };
                ["incremento_total_ventas_cu", "total_vidrio_retornable_mdo_local", "crecer_volumen_fuze_te"].forEach((kpiKey) => {
                    const periodoActual = sumByPath(delegationRows, `${kpiKey}.periodo_actual`);
                    const periodoAnterior = sumByPath(delegationRows, `${kpiKey}.periodo_anterior`);
                    const objetivoCu = sumByPath(delegationRows, `${kpiKey}.objetivo_cu`);
                    totalRow[kpiKey] = {
                        periodo_actual: periodoActual,
                        periodo_anterior: periodoAnterior,
                        objetivo_porcentaje: null,
                        objetivo_cu: objetivoCu,
                        cajas_faltantes: objetivoCu - periodoActual,
                        porcentaje_conseguido: calculateWeightedCanariasProgress(periodoActual, objetivoCu),
                    };
                });
                const clientesPeriodoActual = sumByPath(delegationRows, "crecer_clientes_compradores.periodo_actual");
                const clientesPeriodoAnterior = sumByPath(delegationRows, "crecer_clientes_compradores.periodo_anterior");
                const clientesObjetivo = sumByPath(delegationRows, "crecer_clientes_compradores.objetivo");
                totalRow.crecer_clientes_compradores = {
                    periodo_actual: clientesPeriodoActual,
                    periodo_anterior: clientesPeriodoAnterior,
                    objetivo: clientesObjetivo,
                    clientes_faltantes: clientesPeriodoActual - clientesObjetivo,
                    porcentaje_conseguido: calculateWeightedCanariasProgress(clientesPeriodoActual, clientesObjetivo),
                };
                result.push(totalRow);
            });
            return result;
        };
        const addMadridDelegationTotals = (rows) => {
            const result = [];
            const rowsByDelegation = new Map();
            rows.forEach((row) => {
                var _a, _b;
                const delegation = (_a = row.delegacion) !== null && _a !== void 0 ? _a : "";
                rowsByDelegation.set(delegation, [...((_b = rowsByDelegation.get(delegation)) !== null && _b !== void 0 ? _b : []), row]);
            });
            rowsByDelegation.forEach((delegationRows, delegation) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                result.push(...delegationRows.map((row) => (Object.assign(Object.assign({}, row), { is_total_row: false }))));
                const totalRow = {
                    delegacion: delegation,
                    delegacion_zona_de_ventas: `TOTAL ${delegation}`,
                    distribuidor: `TOTAL ${delegation}`,
                    is_total_row: true,
                };
                totalRow.volumen_mad = {
                    periodo_actual: sumByPath(delegationRows, "volumen_mad.periodo_actual"),
                };
                totalRow.volumen_acumulado_mad = {
                    periodo_actual: sumByPath(delegationRows, "volumen_acumulado_mad.periodo_actual"),
                };
                totalRow.clientes_mad = {
                    periodo_actual: sumByPath(delegationRows, "clientes_mad.periodo_actual"),
                };
                // Ref. Prom.: del_prom es una cifra por delegacion, identica en todas las filas
                // de la delegacion. Se lee de la primera fila, NO se suma.
                const delProm = (_c = (_b = (_a = delegationRows[0]) === null || _a === void 0 ? void 0 : _a.ref_promedio_mad) === null || _b === void 0 ? void 0 : _b.del_prom) !== null && _c !== void 0 ? _c : null;
                totalRow.ref_promedio_mad = {
                    porcentaje_conseguido: delProm,
                    del_prom: delProm,
                };
                // Cajas de regalo: agregar-y-recalcular. Se suman las cajas y la venta bruta
                // crudas de la delegacion, se calcula el ratio agregado (cajas / venta)
                // y se aplica cajas_regalo_result con las constantes min/max de cada fila.
                const sumCajasRegalo = sumByPath(delegationRows, "_raw_cajas_regalo");
                const sumCajasAact = sumByPath(delegationRows, "_raw_aact_cajas_regalo");
                const aggregateRatio = sumCajasAact !== 0 ? (sumCajasRegalo / sumCajasAact) * 100 : 0;
                const cajasMin = (_f = (_e = (_d = delegationRows[0]) === null || _d === void 0 ? void 0 : _d.cajas_regalo_mad) === null || _e === void 0 ? void 0 : _e.min) !== null && _f !== void 0 ? _f : null;
                const cajasMax = (_j = (_h = (_g = delegationRows[0]) === null || _g === void 0 ? void 0 : _g.cajas_regalo_mad) === null || _h === void 0 ? void 0 : _h.max) !== null && _j !== void 0 ? _j : null;
                const cajasResult = FORMULAS.cajas_regalo_result({
                    periodo_actual: aggregateRatio,
                    min: cajasMin,
                    max: cajasMax,
                });
                totalRow.cajas_regalo_mad = Object.assign(Object.assign({}, cajasResult), { porcentaje_conseguido: cajasResult.result, venta_bruta_cajas_regalo: sumCajasRegalo, venta_bruta_aact: sumCajasAact });
                // GDA: sum rtdo (periodo_actual), obj, min, max across routes and recompute per-field
                const gdaKeys = ["gda_ho_f1", "gda_ho_f2", "gda_ho_f3", "gda_al_f1", "gda_al_f2", "gda_al_f3"];
                const gda_mad = {};
                for (const key of gdaKeys) {
                    const path = `gda_mad.${key}`;
                    const sumRtdo = sumByPath(delegationRows, `${path}.periodo_actual`);
                    const sumObj = sumByPath(delegationRows, `${path}._obj`);
                    const sumMin = sumByPath(delegationRows, `${path}.min`);
                    const sumMax = sumByPath(delegationRows, `${path}.max`);
                    gda_mad[key] = Object.assign(Object.assign({}, FORMULAS.madrid_result({ periodo_actual: sumRtdo, min: sumMin, max: sumMax })), { porcentaje_conseguido: FORMULAS.madrid_pct_gda({ rtdo: sumRtdo, obj: sumObj }), _obj: sumObj });
                }
                totalRow.gda_mad = gda_mad;
                result.push(totalRow);
            });
            return result;
        };
        // Initialize dataset
        const dataset = useReportDataset({
            tableData: () => tableData.value,
            tableFields: () => TABLE_FIELDS.value,
            filterFields,
            sortableFields: SORTABLE_FIELDS,
            defaultSorts: DEFAULT_SORTS,
            itemsPerPage: 10,
        });
        const FieldBulkEditDialogRef = ref();
        // Load data
        const loadData = () => __awaiter(this, void 0, void 0, function* () {
            try {
                isLoading.value = true;
                error.value = null;
                const availabilityPromise = hasRedRevisada.value && selectedPeriod.value
                    ? fetchResultsActionAvailability(selectedPeriod.value).catch((availabilityError) => {
                        console.warn("Failed to load results action availability:", availabilityError);
                        return null;
                    })
                    : Promise.resolve(null);
                const [dataResponse, availabilityResponse] = yield Promise.all([
                    fetchReportData(selectedPeriod.value, tab.value),
                    availabilityPromise,
                ]);
                actionAvailability.value = availabilityResponse;
                rawTableData.value = dataResponse;
                // Single fetch builds both the generic titleMap (non-Canarias applyTitles)
                // and the Canarias slot-type variant map. Falls back to empty maps on error.
                const { titleMap, canariasOverrides } = yield fetchObjectiveTitleMaps(tab.value);
                TABLE_FIELDS.value = getTableFields(tableData.value, titleMap, canariasOverrides);
                if (canBulkEditResults.value || canReviewResults.value) {
                    // Load pending changes after data is loaded
                    // Validadores use the supervisor endpoint to see all pending changes
                    const supervisor = canReviewResults.value && !canBulkEditResults.value;
                    try {
                        pendingChanges.value = yield fetchPendingChangesForTable({
                            period: selectedPeriod.value,
                            dataRows: dataResponse,
                        }, tab.value, supervisor);
                    }
                    catch (err) {
                        console.warn("Failed to load pending changes:", err);
                        pendingChanges.value = [];
                    }
                }
                else {
                    pendingChanges.value = [];
                }
                if (canReviewResults.value && selectedPeriod.value) {
                    yield changeReview.loadChangeRequests(selectedPeriod.value, tab.value, true);
                }
                else {
                    changeReview.changeRequests.value = [];
                }
            }
            catch (err) {
                actionAvailability.value = null;
                changeReview.changeRequests.value = [];
                rawTableData.value = [];
                error.value = "Error al cargar los datos";
                console.error("Error loading report data:", err);
            }
            finally {
                isLoading.value = false;
            }
        });
        // Handle bulk edit button click
        const handleRedBulkEdit = () => __awaiter(this, void 0, void 0, function* () {
            if (!FieldBulkEditDialogRef.value)
                return;
            try {
                const result = yield FieldBulkEditDialogRef.value.open();
                // If bulk edit was successful, reload the data and pending changes
                if (result.action === "accept" && result.data === true) {
                    yield loadData();
                }
            }
            catch (error) {
                console.error("Error during bulk edit:", error);
                alert("Error al realizar la edición masiva");
            }
        });
        // Handle review changes button click
        const handleRedReviewChanges = () => __awaiter(this, void 0, void 0, function* () {
            if (!reviewDialogRef.value)
                return;
            try {
                const result = yield reviewDialogRef.value.open();
                // If a change was approved/rejected, reload the data and pending changes
                if (result.action === "accept" && result.data === true) {
                    yield loadData();
                }
            }
            catch (error) {
                console.error("Error during change review:", error);
                alert("Error al revisar los cambios");
            }
        });
        // Watch period changes and reload data
        watch(() => { var _a; return (_a = selectedPeriod.value) === null || _a === void 0 ? void 0 : _a.key; }, (newKey) => {
            if (newKey && selectedPeriod.value) {
                loadData();
            }
        });
        watch(() => tab.value, () => {
            loadData();
        });
        // Load data on mount only if period is already set
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            if (selectedPeriod.value) {
                yield loadData();
            }
        }));
        const __returned__ = { tableZoom, selectedPeriod, isLoading, error, rawTableData, tab, actionAvailability, isNorthReadonlyUser, hasRedRevisada, canRequestResultsChanges, canValidateResultsChanges, canBulkEditResults, canReviewResults, pendingChanges, changeReview, reviewDialogRef, pendingChangeRequestsCount, isLevanteRegionUser, isCanariasRegionUser, isMadridRegionUser, tableData, TABLE_FIELDS, filterFields, displayPendingChanges, dialogRows, bulkEditRows, sumByPath, calculateWeightedCanariasProgress, addCanariasDelegationTotals, addMadridDelegationTotals, dataset, FieldBulkEditDialogRef, loadData, handleRedBulkEdit, handleRedReviewChanges, Panel, Button, ExportButton, PeriodSelector, DatasetFilter, DatasetTable, PaginatorControls, PaginatorInfo, PaginatorItemsPerPage, SortContextMenu, TableZoomDropdown, FieldBulkEditDialog, FieldChangeReviewDialog, get UploadIcon() { return UploadIcon; }, get ClipboardCheckIcon() { return ClipboardCheckIcon; }, get FIELDS() { return FIELDS; }, get PRESET_SORTS() { return PRESET_SORTS; }, LoadingSpinner, get currentUser() { return currentUser; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
